import React, { useEffect, useState } from 'react';
import { Bell, CheckCircle, Clock, Laptop, Pencil, RefreshCw, X, XCircle } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getItResourceRequests, updateItResourceRequestStatus, updateItResourceRequest, nudgeItResourceRequest } from '../api';
import { User } from '../types';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  pending: { label: '待处理', color: 'text-gray-600', bg: 'bg-gray-50', icon: Clock },
  processing: { label: '处理中', color: 'text-primary-600', bg: 'bg-primary-50', icon: RefreshCw },
  completed: { label: '已完成', color: 'text-primary-600', bg: 'bg-primary-50', icon: CheckCircle },
  cancelled: { label: '已取消', color: 'text-gray-500', bg: 'bg-gray-100', icon: XCircle },
};

const URGENCY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  low: { label: '低', color: 'text-gray-500', bg: 'bg-gray-100' },
  normal: { label: '普通', color: 'text-primary-600', bg: 'bg-primary-50' },
  high: { label: '高', color: 'text-gray-600', bg: 'bg-gray-50' },
  urgent: { label: '紧急', color: 'text-gray-600', bg: 'bg-gray-50' },
};

export default function ItResourcePage({ currentUser, notificationBell }: Props) {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [nudgingId, setNudgingId] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ urgency: 'normal', notes: '', expected_onboard_date: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const isIt = currentUser.role === 'it' || currentUser.role === 'admin';
  const canManage = currentUser.role === 'hr' || currentUser.role === 'admin';

  const openEdit = (r: any) => {
    setEditing(r);
    setEditForm({ urgency: r.urgency || 'normal', notes: r.notes || '', expected_onboard_date: r.expected_onboard_date || '' });
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      setSavingEdit(true);
      await updateItResourceRequest(editing.id, editForm);
      toast.success('工单已更新');
      setEditing(null);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '修改失败');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleNudge = async (id: string) => {
    try {
      setNudgingId(id);
      const res = await nudgeItResourceRequest(id);
      toast.success(res?.message || '已催办');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '催办失败');
    } finally {
      setNudgingId(null);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setRequests(await getItResourceRequests());
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '加载IT资源工单失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleStatus = async (id: string, status: string) => {
    try {
      setUpdatingId(id);
      await updateItResourceRequestStatus(id, status);
      toast.success('工单状态已更新');
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '更新失败');
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const counts = requests.reduce((acc: Record<string, number>, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col gap-2.5">
      <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-6 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Laptop size={20} className="text-primary-500" />
            IT资源工单
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">处理待入职员工的账号、设备与系统资源申请 · 共 {requests.length} 单</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading}
            className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-primary-600 hover:border-primary-200 transition-colors disabled:opacity-50">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          {notificationBell}
        </div>
      </div>

      <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-6 py-2 flex items-center gap-1">
        {['pending', 'all', 'processing', 'completed', 'cancelled'].map(key => (
          <button key={key} onClick={() => setFilter(key)}
            className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors', filter === key ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50')}>
            {key === 'all' ? '全部' : STATUS_CONFIG[key].label}
            {key !== 'all' && counts[key] ? <span className="ml-1 text-[10px] text-gray-400">({counts[key]})</span> : null}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
          <Laptop size={48} strokeWidth={1} className="text-gray-300 mb-3" />
          <p className="text-sm">当前暂无IT资源工单</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-2.5">
            {filtered.map(r => {
              const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
              const uc = URGENCY_CONFIG[r.urgency] || URGENCY_CONFIG.normal;
              const StatusIcon = sc.icon;
              return (
                <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600 shrink-0">
                      {(r.candidate_name || '?').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-gray-800">{r.candidate_name}</span>
                        <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5', sc.bg, sc.color)}>
                          <StatusIcon size={10} />{sc.label}
                        </span>
                        <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', uc.bg, uc.color)}>{uc.label}</span>
                        {r.position_name && <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{r.position_name}</span>}
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div><span className="text-gray-400">预计入职时间</span><div className="mt-0.5 font-medium text-gray-700">{r.expected_onboard_date || '-'}</div></div>
                        <div><span className="text-gray-400">汇报人</span><div className="mt-0.5 font-medium text-gray-700">{r.report_to || '-'}</div></div>
                        <div><span className="text-gray-400">发起人</span><div className="mt-0.5 font-medium text-gray-700">{r.created_by_name || '-'}</div></div>
                        <div><span className="text-gray-400">处理人</span><div className="mt-0.5 font-medium text-gray-700">{r.processed_by_name || '-'}</div></div>
                      </div>
                      {r.notes && <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">{r.notes}</div>}
                    </div>
                    {r.status !== 'completed' && r.status !== 'cancelled' && (
                      <div className="flex items-center gap-2 shrink-0">
                        {canManage && (
                          <>
                            <button onClick={() => openEdit(r)}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors inline-flex items-center gap-1">
                              <Pencil size={12} />编辑
                            </button>
                            <button onClick={() => handleNudge(r.id)} disabled={nudgingId === r.id}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 inline-flex items-center gap-1">
                              <Bell size={12} />{nudgingId === r.id ? '催办中...' : '催办'}
                            </button>
                          </>
                        )}
                        {isIt && (
                          <>
                            {r.status === 'pending' && (
                              <button onClick={() => handleStatus(r.id, 'processing')} disabled={updatingId === r.id}
                                className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50">
                                开始处理
                              </button>
                            )}
                            <button onClick={() => handleStatus(r.id, 'completed')} disabled={updatingId === r.id}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50">
                              标记完成
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-800">编辑工单 · {editing.candidate_name}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">紧急程度</label>
                <div className="grid grid-cols-4 gap-2">
                  {(['low', 'normal', 'high', 'urgent'] as const).map(u => (
                    <button key={u} onClick={() => setEditForm(f => ({ ...f, urgency: u }))}
                      className={clsx('py-2 text-xs font-medium rounded-lg border transition-colors',
                        editForm.urgency === u ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
                      {URGENCY_CONFIG[u].label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">预计入职时间</label>
                <input type="date" value={editForm.expected_onboard_date}
                  onChange={e => setEditForm(f => ({ ...f, expected_onboard_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">资源信息 / 备注</label>
                <textarea value={editForm.notes} rows={3}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="账号、设备、系统权限等需求"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary-400 resize-none" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
              <button onClick={handleSaveEdit} disabled={savingEdit}
                className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
                {savingEdit ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
